import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Grid3x3, ChevronDown, ChevronUp, Globe2, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { UNKNOWN_SITE } from "@/lib/coverage";
import { scopeOptions } from "@/lib/scope";
import { useAuth } from "@/context/AuthContext";
import { ScopePicker, useActiveLocations } from "@/components/ScopePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LoadingRow, EmptyState } from "./shared/ui";
import { GeneralCoverage } from "./GeneralCoverage";
import { FunctieCoverage } from "./FunctieCoverage";

// Round K — Beheer → Locaties → Dekking. One server function computes everything; this only draws it.
export function CoverageAdmin({ refreshKey }) {
  const [data, setData] = useState(null);
  const [cell, setCell] = useState(null); // { question, location }
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get("/admin/coverage").then(({ data: d }) => setData(d)).catch(() => setData({ locations: [], questions: [], per_location: [] }));
  }, [refreshKey, tick]);

  return (
    <section className="mt-10 pt-8 border-t border-tw" data-testid="admin-coverage-panel">
      <div className="flex items-center gap-2 mb-3">
        <Grid3x3 size={14} className="text-tw-yellow" strokeWidth={2.5} />
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">DEKKING</div>
      </div>
      <p className="text-xs text-tw-muted mb-5 max-w-xl leading-relaxed">
        Vragen waarvan het antwoord per vestiging verschilt, tegen de actieve locaties die meetellen voor
        dekking. Geel is bekend. Ook antwoorden uit een doorvraag tellen mee. Een standaard "geldt overal"
        telt hier niet als dekking voor elke locatie — alleen als iemand dat zelf heeft aangeklikt.
      </p>
      {!data ? <LoadingRow /> : !data.questions.length ? (
        <EmptyState label="Nog geen vragen die per locatie verschillen" hint="Zet in Beheer → Vragen 'Verschilt per locatie' aan." testId="admin-coverage-empty" />
      ) : (
        <>
          <CoverageSummary perLocation={data.per_location} />
          <div className="hidden md:block"><CoverageGrid data={data} onOpen={setCell} /></div>
          <div className="md:hidden"><CoverageBlocks data={data} onOpen={setCell} /></div>
        </>
      )}
      {data && <FunctieCoverage refreshKey={`${refreshKey}|${tick}`} />}
      {data && <GeneralCoverage general={data.general} />}
      <CoverageCellDialog cell={cell} onClose={() => setCell(null)} onChanged={() => setTick((t) => t + 1)} />
    </section>
  );
}

const CoverageSummary = ({ perLocation }) => (
  <div className="mb-5 flex flex-wrap gap-2" data-testid="admin-coverage-summary">
    {perLocation.map((p) => (
      <span key={p.name} data-testid={`admin-coverage-summary-${p.name}`}
        className="font-mono text-[10px] uppercase tracking-[0.12em] border border-tw px-2 py-1 text-white">
        {p.name} <span className="text-tw-yellow">{p.known} van {p.total} bekend</span>
      </span>
    ))}
  </div>
);

function CoverageGrid({ data, onOpen }) {
  return (
    <div className="overflow-x-auto border border-tw" data-testid="admin-coverage-grid">
      <table className="border-collapse text-left">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-tw-black min-w-[280px] px-3 py-2 font-mono text-[10px] tracking-[0.2em] text-tw-muted">VRAAG</th>
            {data.locations.map((loc) => (
              <th key={loc} className="px-1 py-2 align-bottom">
                <span className="block font-mono text-[10px] uppercase tracking-[0.12em] text-tw-muted [writing-mode:vertical-rl] rotate-180 max-h-28">{loc}</span>
              </th>
            ))}
            <th className="px-2 py-2 align-bottom font-mono text-[10px] tracking-[0.12em] text-tw-muted whitespace-nowrap">zonder locatie</th>
          </tr>
        </thead>
        <tbody>
          {data.questions.map((q) => (
            <tr key={q.id} className="border-t border-tw" data-testid={`admin-coverage-row-${q.id}`}>
              <td className="sticky left-0 z-10 bg-tw-black px-3 py-2 text-xs text-white leading-snug max-w-[340px]">{q.text}</td>
              {data.locations.map((loc) => {
                const known = q.cells[loc] > 0;
                return (
                  <td key={loc} className="p-1 text-center">
                    <button type="button" onClick={() => onOpen({ question: q, location: loc })}
                      data-testid={`admin-coverage-cell-${q.id}-${loc}`} data-known={known ? "true" : "false"}
                      title={known ? `${loc}: bekend` : `${loc}: nog open`}
                      className={`tw-press w-7 h-7 border transition-colors duration-150 ${known ? "bg-tw-yellow border-tw-yellow hover:bg-[#FFDE33]" : "border-tw hover:border-tw-yellow"}`} />
                  </td>
                );
              })}
              <td className="p-1 text-center">
                <button type="button" disabled={!q.unknown_count} onClick={() => onOpen({ question: q, location: UNKNOWN_SITE })}
                  data-testid={`admin-coverage-unknown-${q.id}`}
                  className="tw-press min-w-7 h-7 px-2 font-mono text-[11px] text-white border border-tw hover:border-tw-yellow disabled:text-tw-muted disabled:hover:border-tw">
                  {q.unknown_count}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CoverageBlocks({ data, onOpen }) {
  const [openLoc, setOpenLoc] = useState(null);
  const unknown = data.questions.filter((q) => q.unknown_count > 0);
  return (
    <div className="space-y-2" data-testid="admin-coverage-blocks">
      {unknown.length > 0 && (
        <CoverageBlock name="Zonder locatie" testKey="zonder" open={openLoc === UNKNOWN_SITE}
          onToggle={() => setOpenLoc(openLoc === UNKNOWN_SITE ? null : UNKNOWN_SITE)}
          summary={`${unknown.length} ${unknown.length === 1 ? "vraag" : "vragen"}`} gaps={unknown} known={[]}
          count={(q) => q.unknown_count} onOpen={(q) => onOpen({ question: q, location: UNKNOWN_SITE })} />
      )}
      {data.per_location.map((p) => (
        <CoverageBlock key={p.name} name={p.name} testKey={p.name} open={openLoc === p.name}
          onToggle={() => setOpenLoc(openLoc === p.name ? null : p.name)} summary={`${p.known} van ${p.total} bekend`}
          gaps={data.questions.filter((q) => !(q.cells[p.name] > 0))} known={data.questions.filter((q) => q.cells[p.name] > 0)}
          count={(q) => q.cells[p.name]} onOpen={(q) => onOpen({ question: q, location: p.name })} />
      ))}
    </div>
  );
}

function CoverageBlock({ name, testKey, open, onToggle, summary, gaps, known, count, onOpen }) {
  const [showKnown, setShowKnown] = useState(false);
  const item = (q, kind) => (
    <li key={`${kind}-${q.id}`}>
      <button type="button" onClick={() => onOpen(q)} data-testid={`admin-coverage-${kind}-${testKey}-${q.id}`}
        className="tw-press w-full flex items-start gap-2 text-left px-4 py-3 text-sm text-white hover:text-tw-yellow break-words">
        <span className="flex-1 min-w-0">{q.text}</span>
        {kind !== "gap" && <span className="font-mono text-[10px] text-tw-yellow shrink-0">{count(q)}</span>}
      </button>
    </li>
  );
  return (
    <div className="border border-tw bg-tw-surface" data-testid={`admin-coverage-block-${testKey}`}>
      <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`admin-coverage-block-toggle-${testKey}`}
        className="tw-press w-full flex items-center justify-between gap-2 px-4 py-3 text-left">
        <span className="text-sm text-white font-medium">{name}</span>
        <span className="flex items-center gap-2 font-mono text-[10px] text-tw-yellow">
          {summary} {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>
      {open && (
        <ul className="border-t border-tw divide-y divide-tw">
          {testKey === "zonder" ? gaps.map((q) => item(q, "unknown")) : (
            <>
              {gaps.length === 0 && <li className="px-4 py-3 text-xs text-tw-muted">Alles bekend voor {name}.</li>}
              {gaps.map((q) => item(q, "gap"))}
              {known.length > 0 && (
                <li>
                  <button type="button" onClick={() => setShowKnown((v) => !v)} data-testid={`admin-coverage-known-toggle-${testKey}`}
                    className="tw-press w-full text-left px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-tw-muted hover:text-white">
                    {showKnown ? "Verberg" : "Toon"} bekende vragen ({known.length})
                  </button>
                </li>
              )}
              {showKnown && known.map((q) => item(q, "known"))}
            </>
          )}
        </ul>
      )}
    </div>
  );
}

function CoverageCellDialog({ cell, onClose, onChanged }) {
  const [state, setState] = useState({ loading: false, memos: [], error: null });
  const [reload, setReload] = useState(0);
  const lastCell = useRef(null);
  useEffect(() => {
    if (!cell) { lastCell.current = null; return undefined; }
    const fresh = lastCell.current !== cell;
    lastCell.current = cell;
    let alive = true;
    setState((s) => ({ loading: fresh, memos: fresh ? [] : s.memos, error: null }));
    api.get(`/admin/coverage/${cell.question.id}/memos`, { params: { location: cell.location } })
      .then(({ data }) => alive && setState({ loading: false, memos: data.memos, error: null }))
      .catch((e) => alive && setState({ loading: false, memos: [], error: formatApiError(e) }));
    return () => { alive = false; };
  }, [cell, reload]);
  const changed = () => { setReload((r) => r + 1); onChanged(); };
  const label = cell?.location === UNKNOWN_SITE ? "Antwoorden zonder locatie" : cell?.location;
  return (
    <Dialog open={!!cell} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-tw-surface border-tw text-white rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-coverage-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg leading-snug pr-6">{cell?.question.text}</DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.2em] text-tw-yellow">{label}</DialogDescription>
        </DialogHeader>
        {state.loading ? (
          <div className="flex items-center gap-2 text-tw-muted text-sm"><Loader2 size={14} className="animate-spin" /> Laden…</div>
        ) : state.error ? (
          <p className="text-sm text-tw-red">{state.error}</p>
        ) : state.memos.length === 0 ? (
          <p className="text-sm text-tw-muted" data-testid="admin-coverage-dialog-empty">Nog niemand heeft deze vraag voor {label} beantwoord.</p>
        ) : (
          <ul className="divide-y divide-tw" data-testid="admin-coverage-dialog-memos">
            {state.memos.map((m) => (
              <li key={m.id} className="py-3" data-testid={`admin-coverage-memo-${m.id}`}>
                <div className="font-mono text-[10px] text-tw-muted mb-1">
                  {m.user_name}{m.user_location ? ` · hoort bij ${m.user_location}` : ""}{m.parent_id ? " · doorvraag" : ""} · {new Date(m.created_at).toLocaleDateString("nl-NL")}
                </div>
                <div className="text-sm text-white font-medium">{m.auto_title || m.question}</div>
                <p className="text-xs text-tw-muted mt-1 leading-relaxed">{m.snippet}</p>
                <MemoPlacement memo={m} onDone={changed} />
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Round L — place a memo from the dialog: confirm "Geldt overal" in one click, or pick a location.
function MemoPlacement({ memo, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const active = useActiveLocations(open);
  const options = scopeOptions(user, active).filter((o) => o.site);
  const send = async (body, done) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/recordings/${memo.id}/scope`, body);
      toast.success(data.scope === "all" ? "Geldt nu overal." : `Hoort nu bij ${data.user_location}.`);
      setOpen(false);
      done();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const btn = "tw-press h-8 px-3 inline-flex items-center gap-1.5 border font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-150 disabled:opacity-50";
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      <button type="button" disabled={busy} onClick={() => send({ scope: "all" }, onDone)} data-testid={`admin-coverage-memo-all-${memo.id}`}
        className={`${btn} border-tw-yellow/60 text-tw-yellow hover:bg-tw-yellow hover:text-black`}>
        <Globe2 size={11} strokeWidth={2.5} /> Geldt overal
      </button>
      <ScopePicker open={open} onOpenChange={setOpen} title="Hoort bij" options={options} busy={busy}
        isCurrent={(o) => (memo.user_location || "").toLowerCase() === o.site.toLowerCase()}
        onPick={(o) => send(o.body, onDone)} testId={`admin-coverage-memo-site-${memo.id}`}
        trigger={<button type="button" disabled={busy} data-testid={`admin-coverage-memo-site-${memo.id}`}
          className={`${btn} border-tw text-white hover:border-tw-yellow`}>
          {busy ? <Loader2 size={11} className="animate-spin" /> : <MapPin size={11} strokeWidth={2.5} />} Hoort bij <ChevronDown size={11} />
        </button>} />
    </div>
  );
}
